import React, { useState, useEffect } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { cleanHtml, replaceSmartQuotes, getOrdinal } from "../../utils/procurementForm.utils";

export default function ProcurementAwardCriteriaSection({
  criteria,
  totalPond,
  sanitizePond,
  onChangeField,
  onChangeDescription,
  removeCriteria,
  addCriteria,
  isCriterionComplete,
  onCleanedCriteriaChange,
}) {

  const getDescriptionValue = desc => typeof desc === "string" ? desc : "";

  const [cleanedDescriptions, setCleanedDescriptions] = useState(
    criteria ? criteria.map(() => "") : [],
  );

  useEffect(() => {
    setCleanedDescriptions((prev) => {
      if (!criteria) return [];
      if (criteria.length > prev.length) {
        return [...prev, ...Array(criteria.length - prev.length).fill("")];
      } else if (criteria.length < prev.length) {
        return prev.slice(0, criteria.length);
      }
      return prev;
    });
  }, [criteria, criteria.length]);

  useEffect(() => {
    if (typeof onCleanedCriteriaChange === "function") {
      onCleanedCriteriaChange(cleanedDescriptions);
    }
  }, [cleanedDescriptions, onCleanedCriteriaChange]);

  return (
    <fieldset className="border border-gray-300 p-6 rounded">
      <legend className="block text-lg font-semibold bg-blue-100 px-4 py-2 leading-[1.6] rounded">
        Critères d'attribution du marché
      </legend>
      <div style={{ maxWidth: 960, margin: "auto", padding: 16 }}>
        <div
          style={{
            background: "#fdfdfd",
            border: "1px solid #ccc",
            borderRadius: 8,
            padding: 16,
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          }}
        >
          {totalPond !== 100 && (
            <div
              style={{
                backgroundColor: "#ffe0e0",
                color: "#b00000",
                padding: 8,
                marginBottom: 12,
                borderRadius: 4,
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              ⚠️ Le total des pondérations doit être exactement égal à 100
              (actuel : {totalPond.toFixed(0)})
            </div>
          )}
          {criteria.map((criterion, index) => {
            const labelPlaceholder = `Nom du ${getOrdinal(index)} critère`;
            const pondPlaceholder = `Pondération`;
            const descriptionPlaceholder = `Description du ${getOrdinal(index)} critère...`;

            const handlePondInput = e => onChangeField(criterion.id, "pond", sanitizePond(e.target.value));

            return (
              <div
                key={criterion.id}
                style={{
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  padding: 12,
                  marginBottom: 16,
                  backgroundColor: "#fff",
                }}
              >
                <div
                  style={{
                    fontWeight: "bold",
                    fontSize: 16,
                    marginBottom: 8,
                  }}
                >
                  📝 Critère {index + 1}
                </div>
                <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                  <div style={{ flex: 1 }}>
                    {criterion.fixed
                      ? (
                        <div style={{ fontWeight: "bold", paddingTop: 6 }}>
                          {criterion.label}
                        </div>
                      )
                      : (
                        <input
                          type="text"
                          value={criterion.label}
                          placeholder={labelPlaceholder}
                          onChange={e => onChangeField(criterion.id, "label", e.target.value)}
                          style={{
                            width: "100%",
                            padding: "6px",
                            border: "1px solid #ccc",
                            borderRadius: 4,
                          }}
                        />
                      )}
                  </div>
                  <div style={{ flex: "0 0 160px" }}>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={3}
                      value={criterion.pond}
                      placeholder={pondPlaceholder}
                      onInput={handlePondInput}
                      onPaste={handlePondInput}
                      onChange={handlePondInput}
                      style={{
                        width: "100%",
                        padding: "6px",
                        border: "1px solid #ccc",
                        borderRadius: 4,
                      }}
                    />
                  </div>
                </div>
                {criterion.fixed
                  ? (
                    <div
                      style={{
                        padding: "10px",
                        border: "1px dashed #ccc",
                        borderRadius: 4,
                        backgroundColor: "#fafafa",
                      }}
                      dangerouslySetInnerHTML={{ __html: criterion.description }}
                    />
                  )
                  : (
                    <Editor
                      apiKey="tdmkfcmx36wjtguc5w8kcc91k37keduhkcroi36btoigb6wp"
                      value={getDescriptionValue(criterion.description)}
                      onEditorChange={(newContent) => {
                        onChangeDescription(criterion.id, newContent);
                        setCleanedDescriptions((prev) => {
                          const copy = [...prev];
                          copy[index] = cleanHtml(newContent);
                          return copy;
                        });
                      }}
                      init={{
                        height: 320,
                        menubar: false,
                        plugins: "link visualblocks lists",
                        toolbar: "bold italic underline strikethrough | link | bullist numlist | removeformat",
                        toolbar_mode: "sliding",
                        paste_as_text: false,
                        paste_remove_styles: true,
                        paste_webkit_styles: '',
                        width: '100%',
                        language: 'fr_FR',
                        statusbar: true,
                        branding: false,
                        elementpath: false,
                        resize: true,
                        placeholder: descriptionPlaceholder,
                        setup: (editor) => {
                          editor.on('PastePreProcess', (e) => {
                            let html = e.content;
                            html = replaceSmartQuotes(html);
                            html = html.replace(/(\r?\n){2,}/g, '\n');
                            e.content = html;
                          });
                        },
                      }}
                    />
                  )}
                {!criterion.fixed && (
                  <div style={{ textAlign: "right", marginTop: 8 }}>
                    <button
                      onClick={() => removeCriteria(criterion.id)}
                      className="bg-red-100 text-red-800"
                      style={{
                        border: "none",
                        padding: "4px 18px",
                        cursor: "pointer",
                        borderRadius: 15,
                        fontSize: 13,
                        fontWeight: "bold",
                      }}
                      type="button"
                    >
                      Supprimer ce critère
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          <div
            style={{
              textAlign: "right",
              fontWeight: "bold",
              fontSize: 16,
              color: totalPond !== 100 ? "red" : "green",
              marginTop: 12,
            }}
          >
            Total des pondérations&nbsp;: {totalPond.toFixed(0)}
          </div>
        </div>
        <button
          onClick={addCriteria}
          style={{
            marginTop: 16,
            padding: "6px 12px",
            cursor: isCriterionComplete(criteria[criteria.length - 1])
              ? "pointer"
              : "not-allowed",
            borderRadius: 4,
            backgroundColor: isCriterionComplete(
              criteria[criteria.length - 1],
            )
              ? "#3b82f6"
              : "#ccc",
            color: "white",
            border: "none",
            fontWeight: "bold",
            fontSize: 14,
            width: "auto",
          }}
          type="button"
          disabled={!isCriterionComplete(criteria[criteria.length - 1])}
          title={
            isCriterionComplete(criteria[criteria.length - 1])
              ? "Ajouter un critère"
              : "Complétez le critère en cours avant d'ajouter"
          }
        >
          + Ajouter un critère
        </button>
      </div>
    </fieldset>
  );
}
